/*
 * Narutieum Monitor
 * Copyright (c) 2026 Narutieum
 * Licensed under GPLv3.
 * License guide: https://narutieum.com/bbs/content.php?co_id=naru_license
 */

import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import LoginPage from "./pages/login/LoginPage";
import MainPage from "./pages/main/MainPage";
import GroupListPage from "./pages/groups/GroupListPage";
import GroupDevicesPage from "./pages/groups/GroupDevicesPage";
import DeviceListPage from "./pages/devices/DeviceListPage";
import DeviceMonitorPage from "./pages/devices/DeviceMonitorPage";
import ItemDebugPage from "./pages/items/ItemDebugPage";
import { clearMonitorSession, getStoredMonitorSession, monitorMe } from "./api/client";

function ProtectedRoute({ children }) {
  const session = getStoredMonitorSession();
  const [checking, setChecking] = React.useState(Boolean(session));
  const [verified, setVerified] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    if (!session) {
      setChecking(false);
      setVerified(false);
      return () => {
        mounted = false;
      };
    }

    monitorMe()
      .then(() => {
        if (!mounted) return;
        setVerified(true);
      })
      .catch(() => {
        if (!mounted) return;
        clearMonitorSession();
        setVerified(false);
      })
      .finally(() => {
        if (!mounted) return;
        setChecking(false);
      });

    return () => {
      mounted = false;
    };
  }, [session]);

  if (!session) {
    return <Navigate to="/login" replace />;
  }
  if (checking) {
    return null;
  }
  if (!verified) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/main" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/main"
        element={
          <ProtectedRoute>
            <MainPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monitor"
        element={
          <ProtectedRoute>
            <GroupListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monitor/groups/:groupCode"
        element={
          <ProtectedRoute>
            <GroupDevicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monitor/devices"
        element={
          <ProtectedRoute>
            <DeviceListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monitor/devices/:deviceKey"
        element={
          <ProtectedRoute>
            <DeviceMonitorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/monitor/devices/:deviceKey/items/:itemId"
        element={
          <ProtectedRoute>
            <ItemDebugPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
